import { BlockedBanner } from '@components'

import { Attachment, Flex, Space } from '@billy/design-system'
import lowerFirst from 'lodash/lowerFirst'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { EInvoiceStatus } from '@features/eInvoicing/constants/eInvoiceStatus'
import { EInvoicingReasonLabelSkeleton } from '@features/eInvoicing/elements/EInvoicingReasonLabelSkeleton'
import { useGetEInvoiceReasonLabel } from '@features/eInvoicing/hooks/useGetEInvoiceReasonLabel'
import type { AttachmentEInvoiceTypeBadgeConfig } from '@features/eInvoicing/utils/getAttachmentEInvoiceTypeBadge'

import { useAttachmentPreviewModal } from '../../../context/attachmentPreviewModalContext'

export interface GetBannerMessageProps {
  attachment: Attachment | undefined
  typeBadge?: AttachmentEInvoiceTypeBadgeConfig | null
}

export function EInvoiceBlockedBanner({ attachment, typeBadge }: GetBannerMessageProps): ReactElement {
  const { t } = useTranslation()
  const { eInvoiceStatus, reasonCode } = useAttachmentPreviewModal()

  const isDisputedOrSuspended =
    eInvoiceStatus === EInvoiceStatus.Disputed || eInvoiceStatus === EInvoiceStatus.Suspended

  const { isLoading, reasonLabel } = useGetEInvoiceReasonLabel({
    status: isDisputedOrSuspended ? eInvoiceStatus : EInvoiceStatus.Suspended,
    eInvoiceId: isDisputedOrSuspended ? attachment?.eInvoiceId?.toString() : undefined,
    reasonCode,
  })
  const isResolvingReason = isLoading && !!reasonCode && !reasonLabel

  const variant = isDisputedOrSuspended ? 'warning' : 'info'
  let message = ''

  if (!isDisputedOrSuspended && eInvoiceStatus !== EInvoiceStatus.Refused) {
    return <></>
  }
  if (eInvoiceStatus === EInvoiceStatus.Refused) {
    message = t('attachment_preview_modal.details.blocked_banner_message_refused')
  } else if (isDisputedOrSuspended) {
    message = t('attachment_preview_modal.details.blocked_banner_message', {
      status: lowerFirst(t(typeBadge?.labelKey ?? '')),
      ReasonName: reasonLabel ?? reasonCode,
    })
  }

  return (
    <>
      {isResolvingReason ? (
        <EInvoicingReasonLabelSkeleton />
      ) : (
        <>
          <Flex>
            <BlockedBanner message={message} variant={variant} />
          </Flex>
          <Space size="s16" />
        </>
      )}
    </>
  )
}
